import { LitElement, html, css } from 'lit';
import '../organisms/crusher-table.js';

/**
 * CrusherInvoiceList
 *
 * Billing-page primitive — displays past invoices in a table with predefined
 * columns and consistent formatting. Composes `crusher-table` for pagination
 * + sort + a11y; layers invoice-specific column rendering (formatted dates,
 * currency-aware amounts, status badges, download links) on top.
 *
 * Designed for products using a billing provider (Polar, Stripe, Paddle) that
 * exposes invoices via API. Consumer fetches the invoice list and passes it
 * via the `invoices` property; this component handles presentation.
 *
 * Properties:
 *   - invoices       — array of invoice objects:
 *                       { id, number?, date, amount, currency?, status,
 *                         download_url?, hosted_url? }
 *                      `amount` is in major units (e.g., 12.50 = $12.50);
 *                      pass `amount` in minor units only if you also pass
 *                      `amount-in-cents` boolean (rare).
 *   - currency       — fallback currency code (default "USD"); per-invoice
 *                      `currency` field wins if set
 *   - locale         — fallback locale for date + amount formatting
 *                      (default "en-US")
 *   - loading        — boolean; shows a "Loading invoices…" placeholder
 *                      instead of the table
 *   - empty-message  — shown when `invoices` is empty + not loading
 *                      (default "No invoices yet.")
 *   - amount-in-cents — boolean; if true, amounts are minor units and get
 *                       divided by 100 before formatting
 *   - page-size      — pagination (default 10; pass 0 for no pagination)
 *
 * Slots:
 *   - empty — overrides the built-in empty-state markup when no invoices
 *
 * Events:
 *   - invoice-download — { invoice } — fires when the download/view link
 *                        is clicked. Default href behaviour (link follows)
 *                        is preserved; consumers can `preventDefault()`
 *                        to take over (e.g., to add tracking).
 *
 * A11y: inherits crusher-table's row+column a11y (role=table, caption etc).
 */
export class CrusherInvoiceList extends LitElement {
  static properties = {
    invoices:      { type: Array },
    currency:      { type: String, reflect: true },
    locale:        { type: String, reflect: true },
    loading:       { type: Boolean, reflect: true },
    emptyMessage:  { type: String, attribute: 'empty-message', reflect: true },
    amountInCents: { type: Boolean, attribute: 'amount-in-cents', reflect: true },
    pageSize:      { type: Number, attribute: 'page-size', reflect: true }
  };

  constructor() {
    super();
    this.invoices = [];
    this.currency = 'USD';
    this.locale = 'en-US';
    this.loading = false;
    this.emptyMessage = 'No invoices yet.';
    this.amountInCents = false;
    this.pageSize = 10;
  }

  static styles = css`
    :host {
      display: block;
    }

    .loading,
    .empty {
      padding: var(--crusher-spacing-6);
      text-align: center;
      color: var(--crusher-fg-muted);
      font-size: var(--crusher-font-size-sm);
      border: var(--crusher-component-border-weight) solid var(--crusher-border);
      border-radius: var(--crusher-radius-md);
      background: var(--crusher-surface);
    }

    /* Status badge styles — rendered inside crusher-table cells via column.render */
    .invoice-status {
      display: inline-flex;
      align-items: center;
      gap: var(--crusher-spacing-1);
      padding: 0.125rem 0.5rem;
      border-radius: var(--crusher-radius-full);
      font-size: var(--crusher-font-size-xs);
      font-weight: var(--crusher-font-weight-medium);
      line-height: 1.4;
      text-transform: capitalize;
    }
    .invoice-status[data-tone="success"] {
      background: color-mix(in srgb, var(--crusher-feedback-success) 14%, transparent);
      color: var(--crusher-feedback-success);
    }
    .invoice-status[data-tone="warning"] {
      background: color-mix(in srgb, var(--crusher-feedback-warning) 14%, transparent);
      color: var(--crusher-feedback-warning);
    }
    .invoice-status[data-tone="danger"] {
      background: color-mix(in srgb, var(--crusher-feedback-danger) 14%, transparent);
      color: var(--crusher-feedback-danger);
    }
    .invoice-status[data-tone="neutral"] {
      background: color-mix(in srgb, var(--crusher-fg) 8%, transparent);
      color: var(--crusher-fg-muted);
    }

    .invoice-download {
      color: var(--crusher-color-brand-primary);
      text-decoration: none;
      font-weight: var(--crusher-font-weight-medium);
      font-size: var(--crusher-font-size-sm);
    }
    .invoice-download:hover {
      text-decoration: underline;
    }
    .invoice-download:focus-visible {
      outline: var(--crusher-focus-width) solid var(--crusher-color-brand-primary);
      outline-offset: 2px;
      border-radius: var(--crusher-radius-sm);
    }
  `;

  /* Map provider-agnostic invoice status → tone for the badge. Covers Polar,
     Stripe, Paddle conventions. Unknown statuses render as neutral. */
  _statusTone(status) {
    const s = (status || '').toLowerCase();
    if (['paid', 'succeeded', 'complete', 'completed'].includes(s)) return 'success';
    if (['open', 'pending', 'processing', 'requires_action'].includes(s)) return 'warning';
    if (['failed', 'past_due', 'uncollectible', 'voided', 'canceled', 'cancelled'].includes(s)) return 'danger';
    return 'neutral';
  }

  _formatDate(value) {
    if (!value) return '—';
    /* Accept ISO strings, Date objects, or millisecond timestamps. */
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return String(value);
    return date.toLocaleDateString(this.locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  _formatAmount(amount, currency) {
    if (amount == null) return '—';
    const value = this.amountInCents ? amount / 100 : amount;
    const cur = currency || this.currency;
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: cur
      }).format(value);
    } catch {
      /* Fall back to plain rendering if the currency code is unknown */
      return `${cur} ${value.toFixed(2)}`;
    }
  }

  /* Build the column descriptors for the wrapped crusher-table. The cell
     renderers return DOM nodes per crusher-table's render-hook contract. */
  _buildColumns() {
    const self = this;
    return [
      {
        key: 'date',
        label: 'Date',
        sortable: true,
        render: (row) => {
          const span = document.createElement('span');
          span.textContent = self._formatDate(row.date);
          return span;
        }
      },
      {
        key: 'number',
        label: 'Invoice',
        render: (row) => {
          const span = document.createElement('span');
          span.textContent = row.number ?? row.id ?? '—';
          return span;
        }
      },
      {
        key: 'amount',
        label: 'Amount',
        sortable: true,
        render: (row) => {
          const span = document.createElement('span');
          span.textContent = self._formatAmount(row.amount, row.currency);
          return span;
        }
      },
      {
        key: 'status',
        label: 'Status',
        render: (row) => {
          const span = document.createElement('span');
          span.className = 'invoice-status';
          span.dataset.tone = self._statusTone(row.status);
          span.textContent = (row.status ?? 'unknown');
          return span;
        }
      },
      {
        key: 'download',
        label: '',
        render: (row) => {
          const url = row.download_url || row.hosted_url;
          if (!url) {
            const span = document.createElement('span');
            span.textContent = '—';
            return span;
          }
          const a = document.createElement('a');
          a.className = 'invoice-download';
          a.href = url;
          a.target = '_blank';
          a.rel = 'noopener noreferrer';
          a.textContent = row.download_url ? 'Download' : 'View';
          a.addEventListener('click', (e) => {
            self.dispatchEvent(new CustomEvent('invoice-download', {
              detail: { invoice: row },
              bubbles: true,
              composed: true
            }));
          });
          return a;
        }
      }
    ];
  }

  /* Provide a stable per-row key for crusher-table (re-render efficiency
     + sort stability). Falls back to index if no id/number present. */
  _rowsWithKeys() {
    return (this.invoices || []).map((inv, i) => ({
      ...inv,
      key: inv.id ?? inv.number ?? `idx-${i}`
    }));
  }

  render() {
    if (this.loading) {
      return html`<div class="loading" role="status" aria-live="polite">Loading invoices…</div>`;
    }
    if (!this.invoices || this.invoices.length === 0) {
      const slot = html`<slot name="empty"><div class="empty">${this.emptyMessage}</div></slot>`;
      return slot;
    }

    const cols = this._buildColumns();
    const rows = this._rowsWithKeys();

    return html`
      <crusher-table
        .columns=${cols}
        .rows=${rows}
        .pageSize=${this.pageSize}
      ></crusher-table>
    `;
  }
}

customElements.define('crusher-invoice-list', CrusherInvoiceList);
